import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminSidebar from '../components/AdminSidebar';
import { db } from '../firebase';
import { collection, onSnapshot, query } from 'firebase/firestore';
import { ArrowRight, TrendingUp } from 'lucide-react';

export default function AdminDashboard() {
  const [animals, setAnimals] = useState([]);
  const [donors, setDonors] = useState([]);
  const [couriers, setCouriers] = useState([]);

  useEffect(() => {
    const u1 = onSnapshot(collection(db, 'animals'), s => setAnimals(s.docs.map(d => ({ id: d.id, ...d.data() }))));
    const u2 = onSnapshot(collection(db, 'donors'), s => setDonors(s.docs.map(d => ({ id: d.id, ...d.data() }))));
    const u3 = onSnapshot(collection(db, 'couriers'), s => setCouriers(s.docs.map(d => ({ id: d.id, ...d.data() }))));
    return () => { u1(); u2(); u3(); };
  }, []);

  const sapi = animals.filter(a => a.type === 'sapi');
  const kambing = animals.filter(a => a.type === 'kambing');
  const sembelih = animals.filter(a => ['sembelih', 'packing', 'distribusi', 'selesai'].includes(a.status));
  const packing = animals.filter(a => ['packing', 'distribusi', 'selesai'].includes(a.status));
  const distribusiDone = animals.filter(a => ['distribusi', 'selesai'].includes(a.status));
  const donorDone = donors.filter(d => d.distributionStatus === 'selesai');
  const activeCouriers = couriers.filter(c => c.status === 'active');

  const pct = (a, b) => b > 0 ? Math.round(a / b * 100) : 0;

  const quickStats = [
    { label: 'Total Sapi', value: sapi.length, icon: '🐄', sub: `${animals.filter(a=>a.type==='sapi'&&a.status==='selesai').length} selesai`, link: '/admin/hewan', color: '#166534' },
    { label: 'Total Kambing', value: kambing.length, icon: '🐏', sub: `${animals.filter(a=>a.type==='kambing'&&a.status==='selesai').length} selesai`, link: '/admin/hewan', color: '#d97706' },
    { label: 'Pengkurban', value: donors.length, icon: '👥', sub: `${donorDone.length} sudah terima`, link: '/admin/pengkurban', color: '#0369a1' },
    { label: 'Kurir Aktif', value: activeCouriers.length, icon: '🚚', sub: `${couriers.length} total terdaftar`, link: '/admin/kurir', color: '#7c3aed' },
  ];

  const stages = [
    { label: 'Penyembelihan', done: sembelih.length, total: animals.length, color: '#dc2626', icon: '🔪' },
    { label: 'Packing', done: packing.length, total: animals.length, color: '#d97706', icon: '📦' },
    { label: 'Distribusi', done: distribusiDone.length, total: animals.length, color: '#0369a1', icon: '🚚' },
    { label: 'Distribusi Pengkurban', done: donorDone.length, total: donors.length, color: '#16a34a', icon: '✅' },
  ];

  const recentDonors = donors.slice(-5).reverse();

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-content">
        <div className="page-header">
          <h1>Dashboard Admin</h1>
          <p>Pantau dan kelola seluruh proses qurban dari sini.</p>
        </div>

        {/* Quick stats */}
        <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: '2rem' }}>
          {quickStats.map((s, i) => (
            <Link key={i} to={s.link} style={{ textDecoration: 'none' }}>
              <div className="stat-card" style={{ cursor: 'pointer', transition: 'all 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = 'var(--shadow-md)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow = ''}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div className="stat-label">{s.label}</div>
                    <div className="stat-value" style={{ color: s.color }}>{s.value}</div>
                    <div className="stat-sub">{s.sub}</div>
                  </div>
                  <span style={{ fontSize: 26 }}>{s.icon}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Progress overview */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', marginBottom: '2rem' }}>
          <div className="card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--green-900)' }}>Progres Tahapan</h3>
              <TrendingUp size={18} style={{ color: 'var(--green-600)' }} />
            </div>
            {stages.map((s, i) => (
              <div key={i} style={{ marginBottom: i < stages.length - 1 ? 16 : 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>{s.icon} {s.label}</span>
                  <span style={{ fontSize: 13, color: s.color, fontWeight: 700 }}>{s.done}/{s.total} ({pct(s.done, s.total)}%)</span>
                </div>
                <div className="progress-bar">
                  <div style={{ height: '100%', width: `${pct(s.done, s.total)}%`, background: s.color, borderRadius: 999, transition: 'width 0.5s ease' }} />
                </div>
              </div>
            ))}
          </div>

          {/* Recent donors */}
          <div className="card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--green-900)' }}>Pengkurban Terbaru</h3>
              <Link to="/admin/pengkurban" style={{ fontSize: 12, color: 'var(--green-600)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 3 }}>
                Semua <ArrowRight size={12} />
              </Link>
            </div>
            {recentDonors.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '1.5rem', color: 'var(--text-muted)', fontSize: 14 }}>Belum ada data pengkurban</div>
            ) : (
              recentDonors.map((d, i) => (
                <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: i < recentDonors.length - 1 ? '1px solid var(--border)' : 'none' }}>
                  <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--green-100)', color: 'var(--green-700)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 12 }}>
                    {d.name?.charAt(0)?.toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{d.animalType === 'sapi' ? '🐄' : '🐏'} {d.animalName || '-'}</div>
                  </div>
                  <span className={`badge ${d.distributionStatus === 'selesai' ? 'badge-green' : d.distributionStatus === 'distribusi' ? 'badge-blue' : 'badge-gray'}`} style={{ fontSize: 10 }}>
                    {d.distributionStatus === 'selesai' ? 'Diterima' : d.distributionStatus === 'distribusi' ? 'Dikirim' : 'Menunggu'}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Quick actions */}
        <div className="card">
          <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--green-900)', marginBottom: 14 }}>Aksi Cepat</h3>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {[
              { to: '/admin/hewan', label: '+ Tambah Hewan', color: 'btn-primary' },
              { to: '/admin/pengkurban', label: '+ Tambah Pengkurban', color: 'btn-primary' },
              { to: '/admin/proses', label: '🔪 Update Proses', color: 'btn-ghost' },
              { to: '/admin/distribusi', label: '🚚 Update Distribusi', color: 'btn-ghost' },
              { to: '/admin/kurir', label: '+ Tambah Kurir', color: 'btn-ghost' },
            ].map((a, i) => (
              <Link key={i} to={a.to} className={`btn btn-sm ${a.color}`}>{a.label}</Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
